
import React from 'react';
import { SimulationConfig } from './types';
import { SectionLabel } from './SectionLabel';
import { SmartInput } from './SmartInput';
import { Toggle } from './Toggle';
import { ControlGroup } from './ControlGroup';

interface EscapeAdvancedSectionProps {
  config: SimulationConfig;
  setConfig: React.Dispatch<React.SetStateAction<SimulationConfig>>;
}

export const EscapeAdvancedSection: React.FC<EscapeAdvancedSectionProps> = ({ config, setConfig }) => (
  <div className="w-full flex flex-col gap-3 p-4 rounded-3xl border bg-blue-500/5 border-blue-500/20 mb-4">
    <SectionLabel icon="fa-cog" darkMode={config.darkMode}>Escape Advanced</SectionLabel>
    
    <SmartInput 
        label="Inner Start Radius" 
        min={10} max={500} 
        value={config.containerRadius} 
        onChange={v => setConfig(p => ({...p, containerRadius: v}))} 
        darkMode={config.darkMode} 
    />

    <div className="grid grid-cols-2 gap-3">
        <div className="flex items-center justify-between border border-blue-500/10 p-2 rounded-xl">
          <span className="text-[8px] font-black opacity-60 uppercase text-center">Dynamic Shrink</span>
          <Toggle active={config.escapeDynamicShrink} onClick={() => setConfig(p => ({...p, escapeDynamicShrink: !p.escapeDynamicShrink}))} />
        </div>
        <div className="flex items-center justify-between border border-blue-500/10 p-2 rounded-xl">
          <span className="text-[8px] font-black opacity-60 uppercase text-center">Shrink Mode</span>
          <Toggle active={config.escapeShrinkMode} onClick={() => setConfig(p => ({...p, escapeShrinkMode: !p.escapeShrinkMode}))} />
        </div>
    </div>
    <SmartInput label="Min Radius" min={0} max={100} value={config.escapeMinRadius} onChange={v => setConfig(p => ({...p, escapeMinRadius: v}))} darkMode={config.darkMode} />
    <SmartInput label="Shrink Rate" min={0} max={10} step={0.1} value={config.escapeShrinkRate} onChange={v => setConfig(p => ({...p, escapeShrinkRate: v}))} darkMode={config.darkMode} />
    <SmartInput label="Throw Angle (Deg)" min={0} max={360} value={config.escapeThrowAngle} onChange={v => setConfig(p => ({...p, escapeThrowAngle: v}))} darkMode={config.darkMode} />
    <SmartInput label="Throw Power" min={1} max={100} value={config.escapeThrowPower} onChange={v => setConfig(p => ({...p, escapeThrowPower: v}))} darkMode={config.darkMode} />
    
    <div className="h-px bg-white/10 my-2" />
    <span className="text-[9px] font-black uppercase opacity-60">Shatter Physics (Gravity Dust)</span>
    <SmartInput label="Blast Force" min={0} max={1000} step={10} value={config.shatterForce ?? 100} onChange={v => setConfig(p => ({...p, shatterForce: v}))} darkMode={config.darkMode} />
    <SmartInput label="Gravity" min={0} max={2000} step={50} value={config.shatterGravity ?? 500} onChange={v => setConfig(p => ({...p, shatterGravity: v}))} darkMode={config.darkMode} />

    {/* PERFORMANCE OPTIMIZATION SECTION */}
    <div className="h-px bg-white/10 my-2" />
    <SectionLabel icon="fa-microchip" darkMode={config.darkMode}>Performance Tuner</SectionLabel>
    <div className="flex flex-col gap-3 p-3 rounded-2xl bg-red-500/5 border border-red-500/20">
        <p className="text-[8px] opacity-60 italic text-center">Adjust these if breakage causes lag.</p>
        <SmartInput 
            label="Dust Density" 
            min={0.1} max={1.0} step={0.1}
            value={config.dustResolution ?? 0.5} 
            onChange={v => setConfig(p => ({...p, dustResolution: v}))} 
            darkMode={config.darkMode} 
        />
        <SmartInput 
            label="Max Fragments" 
            min={10} max={500} step={10}
            value={config.maxDustParticles ?? 100} 
            onChange={v => setConfig(p => ({...p, maxDustParticles: v}))} 
            darkMode={config.darkMode} 
        />
        <SmartInput 
            label="Max Active Blasts" 
            min={50} max={500} step={10}
            value={config.maxActiveBlasts ?? 200} 
            onChange={v => setConfig(p => ({...p, maxActiveBlasts: v}))} 
            darkMode={config.darkMode} 
        />
    </div>

    <SectionLabel icon="fa-arrows-split-up-and-left" darkMode={config.darkMode}>Early Stage Overrides</SectionLabel>
    <div className="flex flex-col gap-3 p-3 rounded-2xl bg-black/5 border border-white/5">
        <ControlGroup label="Opposite Gap Rings">
            <div className="flex justify-between items-center mb-1">
                <span className="text-[8px] opacity-60">First N rings rotate 180°</span>
            </div>
            <input 
                type="range" min={0} max={20} step={1} 
                value={config.escapeOppositeGapCount || 0} 
                onChange={e => setConfig(p => ({...p, escapeOppositeGapCount: parseInt(e.target.value)}))} 
                className="w-full"
            />
            <div className="text-right text-[9px] font-mono font-bold">{config.escapeOppositeGapCount || 0} rings</div>
        </ControlGroup>
        
        <SmartInput 
            label="Opposite Gap Size" 
            min={5} max={120} 
            value={config.escapeOppositeGapSize || 30} 
            onChange={v => setConfig(p => ({...p, escapeOppositeGapSize: v}))} 
            darkMode={config.darkMode} 
        />
    </div>

    <div className="h-px bg-white/10 my-2" />
    <SmartInput label="Max Visible Rings" min={10} max={1000} value={config.escapeMaxVisibleRings} onChange={v => setConfig(p => ({...p, escapeMaxVisibleRings: v}))} darkMode={config.darkMode} />
    <SmartInput label="Ring Thickness" min={1} max={30} value={config.escapeRingThickness} onChange={v => setConfig(p => ({...p, escapeRingThickness: v}))} darkMode={config.darkMode} />
    <SmartInput label="Alpha Base" min={0} max={1} step={0.01} value={config.escapeRingAlphaBase} onChange={v => setConfig(p => ({...p, escapeRingAlphaBase: v}))} darkMode={config.darkMode} />
    <SmartInput label="Alpha Fade Curve" min={0} max={2} step={0.01} value={config.escapeRingAlphaFade} onChange={v => setConfig(p => ({...p, escapeRingAlphaFade: v}))} darkMode={config.darkMode} />
    <SmartInput label="Blast Duration (ms)" min={500} max={10000} value={config.blastDuration} onChange={v => setConfig(p => ({...p, blastDuration: v}))} darkMode={config.darkMode} />
  </div>
);
